import React from 'react';
import { Sparkles, BookOpen, Calendar, Users, ArrowRight, Heart, Star } from 'lucide-react';

const FacilitadorasColorTest = () => {
  // Gold/Yellow variations for Facilitadoras
  const goldPalettes = [
    {
      name: 'Dorado Antiguo Profundo',
      primary: '#6b5a3e',
      primaryLight: '#7b6a4e',
      accent: '#d4af37',
      accentLight: '#e6c555',
      description: 'Dorado envejecido, elegante y noble',
      vibe: 'Sabiduría ancestral, tradición, valor'
    },
    {
      name: 'Miel Tostada',
      primary: '#7a6642',
      primaryLight: '#8a7652',
      accent: '#daa520',
      accentLight: '#e8b830',
      description: 'Cálido como la miel, nutritivo',
      vibe: 'Calidez, nutrición, dulzura profunda'
    },
    {
      name: 'Ámbar Cálido',
      primary: '#6d5840',
      primaryLight: '#7d6850',
      accent: '#cc9933',
      accentLight: '#dda944',
      description: 'Ámbar con profundidad terrosa',
      vibe: 'Preservación, luz atrapada, tiempo'
    },
    {
      name: 'Bronce Dorado',
      primary: '#5d4d35',
      primaryLight: '#6d5d45',
      accent: '#c9a227',
      accentLight: '#d9b237',
      description: 'Mezcla de bronce y oro, noble',
      vibe: 'Fortaleza, nobleza, durabilidad'
    },
    {
      name: 'Ocre Luminoso',
      primary: '#7d6a45',
      primaryLight: '#8d7a55',
      accent: '#d4a84b',
      accentLight: '#e4b85b',
      description: 'Ocre con luz interior',
      vibe: 'Tierra iluminada, arte, expresión'
    },
    {
      name: 'Caramelo Oscuro',
      primary: '#5c4a38',
      primaryLight: '#6c5a48',
      accent: '#c8a45a',
      accentLight: '#d8b46a',
      description: 'Tono caramelo profundo y dulce',
      vibe: 'Confort, hogar, calidez envolvente'
    },
    {
      name: 'Mostaza Noble',
      primary: '#6a5d3a',
      primaryLight: '#7a6d4a',
      accent: '#c9a227',
      accentLight: '#d9b237',
      description: 'Mostaza con elegancia',
      vibe: 'Audacia, claridad, verdad'
    },
    {
      name: 'Arena Dorada',
      primary: '#7a6b52',
      primaryLight: '#8a7b62',
      accent: '#d4b896',
      accentLight: '#e4c8a6',
      description: 'Dorado suave, desértico',
      vibe: 'Espacio, silencio, meditación'
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-6">
        <h1 className="text-4xl font-heading text-center mb-4">
          Pruebas de Color: Facilitadoras
        </h1>
        <p className="text-center text-gray-600 mb-4 max-w-2xl mx-auto">
          Variaciones de <strong>tonalidades doradas</strong> para reemplazar el verde actual 
          en la formación de Facilitadoras en Sostén Energético.
        </p>
        <p className="text-center text-sm text-gray-400 mb-12">
          Color actual: <span className="font-mono bg-[#2D3328] text-white px-2 py-1 rounded">#2D3328 (Verde oliva)</span>
        </p>

        <div className="space-y-12">
          {goldPalettes.map((palette, index) => (
            <div key={index} className="border-t pt-10 first:border-t-0 first:pt-0">
              {/* Header */}
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 gap-4">
                <div>
                  <h2 className="text-2xl font-heading mb-1">{palette.name}</h2>
                  <p className="text-gray-500">{palette.description}</p>
                  <p className="text-sm mt-1" style={{ color: palette.primary }}>
                    ✦ {palette.vibe}
                  </p>
                </div>
                <div className="flex gap-2">
                  <div className="text-center">
                    <div 
                      className="w-14 h-14 rounded-xl shadow-md mb-1"
                      style={{ backgroundColor: palette.primary }}
                    />
                    <span className="text-[10px] font-mono text-gray-400">{palette.primary}</span>
                  </div>
                  <div className="text-center">
                    <div 
                      className="w-14 h-14 rounded-xl shadow-md mb-1"
                      style={{ backgroundColor: palette.primaryLight }}
                    />
                    <span className="text-[10px] font-mono text-gray-400">Light</span>
                  </div>
                  <div className="text-center">
                    <div 
                      className="w-14 h-14 rounded-xl shadow-md mb-1"
                      style={{ backgroundColor: palette.accent }}
                    />
                    <span className="text-[10px] font-mono text-gray-400">{palette.accent}</span>
                  </div>
                </div>
              </div>

              {/* Full width preview - simulating Facilitadoras section */}
              <div 
                className="rounded-3xl overflow-hidden shadow-xl"
                style={{ background: `linear-gradient(135deg, ${palette.primary} 0%, ${palette.primaryLight} 100%)` }}
              >
                <div className="p-8 lg:p-12">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                    {/* Content side */}
                    <div>
                      {/* Badge */}
                      <div 
                        className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-6"
                        style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                      >
                        <Sparkles className="w-4 h-4" style={{ color: palette.accent }} />
                        <span className="text-sm text-white/90">Formación Especial · En vivo</span>
                      </div>

                      <h3 className="text-3xl lg:text-4xl font-heading text-white mb-4">
                        Facilitadores en Sostén Energético
                      </h3>
                      <p className="text-white/70 mb-6 leading-relaxed">
                        En este tiempo de grandes transformaciones colectivas, cada vez más almas 
                        están llamadas a sostener, guiar y acompañar procesos tanto individuales 
                        como grupales. Sostener es un arte vibracional.
                      </p>

                      {/* Info badges */}
                      <div className="flex flex-wrap gap-4 mb-8">
                        <div className="flex items-center gap-2 text-white/60">
                          <BookOpen className="w-5 h-5" strokeWidth={1.5} />
                          <span>12 clases online + 3 presenciales</span>
                        </div>
                        <div className="flex items-center gap-2 text-white/60">
                          <Calendar className="w-5 h-5" strokeWidth={1.5} />
                          <span>Marzo – Junio 2026</span>
                        </div>
                        <div className="flex items-center gap-2 text-white/60">
                          <Users className="w-5 h-5" strokeWidth={1.5} />
                          <span>Plazas limitadas</span>
                        </div>
                      </div>

                      {/* CTAs */}
                      <div className="flex flex-col sm:flex-row gap-4">
                        <button 
                          className="px-8 py-4 rounded-full font-medium transition-all inline-flex items-center justify-center gap-2 hover:opacity-90"
                          style={{ backgroundColor: palette.accent, color: 'white' }}
                        >
                          Ver formación completa
                          <ArrowRight className="w-5 h-5" />
                        </button>
                        <div className="flex items-baseline gap-2">
                          <span 
                            className="text-3xl font-heading"
                            style={{ color: palette.accent }}
                          >
                            desde 1.020€
                          </span>
                          <span className="text-white/50 text-sm">pronto pago</span>
                        </div>
                      </div>
                    </div>

                    {/* Image side placeholder */}
                    <div className="relative">
                      <div 
                        className="aspect-[4/3] rounded-3xl flex items-center justify-center"
                        style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}
                      >
                        <div className="text-center p-6">
                          <Heart 
                            className="w-16 h-16 mx-auto mb-4 opacity-30" 
                            style={{ color: palette.accent }}
                            strokeWidth={1}
                          />
                          <p className="text-white/60 font-heading text-lg italic">
                            "Sostener es un arte vibracional"
                          </p>
                        </div>
                      </div>
                      <div 
                        className="absolute -bottom-4 -right-4 text-white px-6 py-3 rounded-full font-medium shadow-lg"
                        style={{ backgroundColor: palette.accent }}
                      >
                        3ª Edición
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Light section preview */}
              <div className="mt-6 p-6 bg-gray-50 rounded-2xl">
                <p className="text-sm text-gray-500 mb-4">Vista en secciones claras:</p>
                <div className="flex flex-wrap gap-3 items-center">
                  <button 
                    className="px-6 py-3 rounded-full text-sm font-medium text-white"
                    style={{ backgroundColor: palette.accent }}
                  >
                    Botón dorado principal
                  </button>
                  <button 
                    className="px-6 py-3 rounded-full text-sm font-medium text-white"
                    style={{ backgroundColor: palette.primary }}
                  >
                    Botón oscuro
                  </button>
                  <button 
                    className="px-6 py-3 rounded-full text-sm font-medium border-2"
                    style={{ borderColor: palette.primary, color: palette.primary }}
                  >
                    Botón outline
                  </button>
                  <div 
                    className="px-4 py-2 rounded-full text-sm"
                    style={{ backgroundColor: `${palette.accent}20`, color: palette.primary }}
                  >
                    Badge suave
                  </div>
                </div>
                <div 
                  className="mt-4 p-4 rounded-xl border-2"
                  style={{ borderColor: palette.primary }}
                >
                  <p className="text-sm" style={{ color: palette.primary }}>
                    <strong>Card destacada:</strong> Así se vería un elemento con borde del color primario
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="mt-16 p-8 bg-gradient-to-br from-amber-50 to-yellow-50 rounded-3xl border border-amber-100">
          <h2 className="text-2xl font-heading mb-6 text-center">🏆 Mi Recomendación para Facilitadoras</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-2xl shadow-sm text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 shadow-md" style={{ backgroundColor: '#6b5a3e' }} />
              <h3 className="font-heading mb-2">🥇 Dorado Antiguo</h3>
              <p className="text-sm text-gray-600">
                Noble, sabio, conecta con la tradición del sostén ancestral. El más elegante.
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 shadow-md" style={{ backgroundColor: '#5d4d35' }} />
              <h3 className="font-heading mb-2">🥈 Bronce Dorado</h3>
              <p className="text-sm text-gray-600">
                Fuerte y cálido. Transmite solidez y confianza para sostener.
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 shadow-md" style={{ backgroundColor: '#7a6642' }} />
              <h3 className="font-heading mb-2">🥉 Miel Tostada</h3>
              <p className="text-sm text-gray-600">
                El más cálido y acogedor. Ideal si buscas máxima calidez.
              </p>
            </div>
          </div>
        </div>

        {/* Quick reference */}
        <div className="mt-8 p-6 bg-gray-900 rounded-2xl text-white">
          <h3 className="font-heading mb-4">📋 Códigos Rápidos - Dorados</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs font-mono">
            <div><span className="text-yellow-400">Dorado Antiguo:</span> #6b5a3e</div>
            <div><span className="text-yellow-400">Miel Tostada:</span> #7a6642</div>
            <div><span className="text-yellow-400">Ámbar Cálido:</span> #6d5840</div>
            <div><span className="text-yellow-400">Bronce Dorado:</span> #5d4d35</div>
            <div><span className="text-yellow-400">Ocre Luminoso:</span> #7d6a45</div>
            <div><span className="text-yellow-400">Caramelo Oscuro:</span> #5c4a38</div>
            <div><span className="text-yellow-400">Mostaza Noble:</span> #6a5d3a</div>
            <div><span className="text-yellow-400">Arena Dorada:</span> #7a6b52</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacilitadorasColorTest;
